import { transformUtil } from '@eventbrite/transformation-utils';
import { Schema } from 'normalizr';
import {
    InstrumentType,
    PaymentConstraintName,
    PaymentMethodType,
    PaymentOptionType,
} from '../../models/paymentMethods/enums';
import {
    PaymentMethodInfo,
    PAYMENT_METHODS_INFO,
} from '../../models/paymentMethods/paymentMethodInfo';
import { UserInstrument } from '../../models/user';
import {
    PaginatedResponse,
    PaginatedResponseFormatted,
} from './pagination.types';
import { SingleUserInstrumentResponse } from './userInstruments';

const paymentOptionsSchema = new Schema('payment_options');

export interface Instrument {
    variants: Array<PaymentConstraintName>;
    type: InstrumentType;
}
export interface PaymentOptionsResponse extends PaginatedResponse {
    payment_options: Array<{
        type: PaymentOptionType;
        instrument_types: Array<Instrument>;
    }>;
    user_instruments?: Array<SingleUserInstrumentResponse>;
}

export interface PaymentOptionsResponseFormatted
    extends PaginatedResponseFormatted {
    paymentOptions: Array<{
        type: PaymentOptionType;
        instrumentTypes: Array<Instrument>;
    }>;
    userInstruments?: Array<UserInstrument>;
}

const getInstrumentForCreditCardPaymentMethod = (
    instrument: Instrument,
    paymentOption: {
        type: PaymentOptionType;
    },
) => {
    return {
        ...instrument,
        type:
            paymentOption.type === PaymentOptionType.AUTHNET
                ? PaymentMethodType.AUTHNET
                : PaymentMethodType.CREDIT,
    };
};

const isBraintreeApplePay = (variant: PaymentConstraintName) =>
    variant === PaymentConstraintName.BRAINTREE_APPLE_PAY;

const isApplePayInVariants = (variants: Array<PaymentConstraintName>) =>
    variants.includes(PaymentConstraintName.BRAINTREE_APPLE_PAY);

const isBraintreeGooglePay = (variant: PaymentConstraintName) =>
    variant === PaymentConstraintName.BRAINTREE_GOOGLE_PAY;

const isGooglePayInVariants = (variants: Array<PaymentConstraintName>) =>
    variants.includes(PaymentConstraintName.BRAINTREE_GOOGLE_PAY);

const getInstrumentsForBraintreeBasedPaymentMethods = (instrument: {
    type: InstrumentType;
    variants: Array<PaymentConstraintName>;
}) => {
    // Splitting Apple Pay to an independent instrument due to the existing coupling between Paypal
    // and NONCE_BRAINTREE in the code
    // TODO: Decoupling Paypal from NONCE_BRAINTREE

    const payPalInstrument = {
        ...instrument,
        variants: instrument.variants.filter(
            (variant) =>
                !isBraintreeApplePay(variant) && !isBraintreeGooglePay(variant),
        ),
    };

    const applePayInstrument = isApplePayInVariants(instrument.variants)
        ? {
              variants: [PaymentConstraintName.BRAINTREE_APPLE_PAY],
              type: PaymentMethodType.APPLE_PAY,
          }
        : null;

    const googlePayInstrument = isGooglePayInVariants(instrument.variants)
        ? {
              variants: [PaymentConstraintName.BRAINTREE_GOOGLE_PAY],
              type: PaymentMethodType.GOOGLE_PAY,
          }
        : null;

    return [payPalInstrument, applePayInstrument, googlePayInstrument].flatMap(
        (item) => (item ? [item] : []),
    );
};

const getInstrument = (
    instrument: Instrument,
    paymentOption: { type: PaymentOptionType },
) => {
    if (instrument.type === InstrumentType.CREDIT_CARD) {
        return [
            getInstrumentForCreditCardPaymentMethod(instrument, paymentOption),
        ];
    }

    if (instrument.type === InstrumentType.NONCE_BRAINTREE) {
        return getInstrumentsForBraintreeBasedPaymentMethods(instrument);
    }

    return [instrument];
};

export const transformPaymentOptions = (data: PaymentOptionsResponse) => {
    const transformedPaymentOptions = transformUtil({
        response: data,
        schema: paymentOptionsSchema,
    });
    const paymentOptions = transformedPaymentOptions.entities.payment_options[
        transformedPaymentOptions.result
    ] as PaymentOptionsResponseFormatted;

    const paymentMethodsInfo = PAYMENT_METHODS_INFO as unknown as Record<
        string,
        PaymentMethodInfo
    >;

    // NOTE: The response from payment_options uses 'CREDIT_CARD' to identify credit cards.
    // The payment_methods endpoint expects 'CREDIT' when we set it. Since payment_options
    // is deprecated, we'll go with the payment_methods version.

    // Replace 'CREDIT_CARD' with 'CREDIT' (when using EPP) or 'AUTHNET' (when using Authnet) in instrument.type.
    const supportedPaymentOptions = paymentOptions.paymentOptions.map(
        (singlePaymentOption) => ({
            ...singlePaymentOption,
            instrumentTypes: singlePaymentOption.instrumentTypes
                .flatMap((instrument) =>
                    getInstrument(instrument, singlePaymentOption),
                )
                // Filter out payment methods we do not support (those we don't have in PAYMENTS_METHODS_INFO)
                .filter((instrument) => paymentMethodsInfo[instrument.type]),
        }),
    );

    return {
        ...paymentOptions,
        paymentOptions: supportedPaymentOptions,
    };
};
